import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import {
  useExhibitList,
  useDeleteExhibit,
  useGenerateExhibitQR,
  useTransitionExhibit,
} from "@shared/api/hooks/useExhibits";
import { ApiError } from "@shared/api/client";
import type { ExhibitListItem } from "@shared/api/exhibits";
import { useAuth } from "@shared/contexts/AuthContext";
import { Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

import ExhibitListRow from "./ExhibitListRow";
import ListFilterBar from "@staff/features/shared/ListFilterBar";
import ContentBatchBar from "@staff/features/shared/ContentBatchBar";
import StatusFilter from "@staff/components/StatusFilter";
import { NarrationHeader } from "@staff/features/narration/NarrationBadges";
import {
  useListRows,
  useListUrlState,
} from "@staff/features/shared/useListPage";

const StaffDashboard = () => {
  const navigate = useNavigate();
  const { role } = useAuth();
  // Analysts read only — no batch selection (the actions would 403).
  const canBatch = role === "admin" || role === "editor";

  // F-U: Status, search and filters in the URL; shared with the Zones page.
  const url = useListUrlState();
  const { status, setStatus, searchQuery, setSearchQuery } = url;
  const { data: exhibits = [], isLoading: loading } = useExhibitList(status);
  const {
    filters,
    options,
    filtered: filteredExhibits,
    hasNarrowing,
    openPage,
    selected,
    setSelected,
    selectableRows,
    selectedRows,
    allSelected,
    toggleOne,
    toggleAll,
  } = useListRows("exhibits", url, exhibits, loading);
  const { setFilters, clearAll } = url;

  const deleteExhibitMutation = useDeleteExhibit();
  const transitionMutation = useTransitionExhibit();
  const generateQRMutation = useGenerateExhibitQR();

  const handleTogglePublish = (
    exhibitId: string,
    currentState: ExhibitListItem["state"],
  ) => {
    const nextState = currentState === "published" ? "draft" : "published";
    transitionMutation.mutate(
      { exhibitId, payload: { to_state: nextState } },
      {
        onSuccess: () => {
          toast.success(
            currentState === "published"
              ? "Exhibit unpublished"
              : "Exhibit published",
          );
        },
        onError: (err) => {
          // Surface the backend error envelope so publish-gate failures
          // (e.g. missing_title_tc per ADR-0005) aren't masked.
          const message =
            err instanceof ApiError && err.message
              ? err.message
              : "Failed to update publish status";
          toast.error(message);
        },
      },
    );
  };

  const handleDelete = (exhibitId: string) => {
    deleteExhibitMutation.mutate(exhibitId, {
      onSuccess: () => toast.success("Exhibit deleted"),
      onError: () => toast.error("Failed to delete exhibit"),
    });
  };

  const handleGenerateQR = (exhibitId: string) => {
    generateQRMutation.mutate(exhibitId, {
      onSuccess: () => toast.success("QR code generated"),
      onError: () => toast.error("Failed to generate QR code"),
    });
  };

  return (
    <div
      className="min-h-screen bg-background"
      data-testid="staff-dashboard-page"
    >
      {/* Header — subpage of the Exhibitions home; the top menu bar
          lives there, so this page only carries a back arrow. */}
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button
            onClick={() => navigate("/staff/exhibitions")}
            variant="ghost"
            size="sm"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-heading font-bold">
            Exhibit Management
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
          <Input
            placeholder="Search by code or title…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-xs"
            data-testid="search-input"
          />
          <StatusFilter value={status} onChange={setStatus} />
          <Button
            onClick={() => openPage("/staff/exhibit/create")}
            className="sm:ml-auto"
            data-testid="create-exhibit-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create New Exhibit
          </Button>
        </div>

        {/* F-U filters */}
        <div className="mb-4">
          <ListFilterBar
            filters={filters}
            options={options}
            onChange={setFilters}
            onClearAll={clearAll}
            canClear={hasNarrowing}
            shownCount={filteredExhibits.length}
          />
        </div>

        {!loading && (
          <p
            className="mb-3 text-sm text-muted-foreground"
            data-testid="result-count"
          >
            Showing {filteredExhibits.length} of {exhibits.length} exhibits
          </p>
        )}

        {canBatch && selectedRows.length > 0 && (
          <ContentBatchBar
            kind="exhibit"
            selectedRows={selectedRows}
            onClearSelection={() => setSelected(new Set())}
            onFinished={(failedIds) => setSelected(new Set(failedIds))}
            onEdit={(id) => openPage(`/staff/exhibit/${id}/edit`)}
          />
        )}

        {/* Exhibits Table */}
        {loading ? (
          <div className="text-center py-12" data-testid="loading-state">
            Loading...
          </div>
        ) : filteredExhibits.length === 0 ? (
          <div
            className="text-center py-12 border border-border rounded-lg"
            data-testid="empty-state"
          >
            <p className="text-muted-foreground">No exhibits found</p>
          </div>
        ) : (
          <div className="bg-white border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="exhibits-table">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    {canBatch && (
                      <th className="pl-6 pr-2 py-3 w-8">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          disabled={selectableRows.length === 0}
                          onChange={(e) => toggleAll(e.target.checked)}
                          aria-label="Select all exhibits in this view"
                          data-testid="select-all-exhibits"
                        />
                      </th>
                    )}
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      Code
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      Image
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      Title (TC)
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      Exhibition
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      Zone
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      Status
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      QR Code
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      <NarrationHeader />
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredExhibits.map((exhibit) => (
                    <ExhibitListRow
                      key={exhibit.id}
                      exhibit={exhibit}
                      query={searchQuery}
                      selected={selected.has(exhibit.id)}
                      onSelect={canBatch ? toggleOne : undefined}
                      onTogglePublish={handleTogglePublish}
                      onGenerateQR={handleGenerateQR}
                      onEdit={(id) => openPage(`/staff/exhibit/${id}/edit`)}
                      onOpenNarration={(id) =>
                        openPage(`/staff/exhibit/${id}/edit#narration`)
                      }
                      onDelete={handleDelete}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffDashboard;
